/**
 * Modelo de BLOQUES del editor visual de email.
 *
 * Un documento es una lista de bloques cuya ESTRUCTURA (orden, imágenes, botones,
 * separadores) se comparte entre idiomas; solo el TEXTO varía por idioma:
 *
 *   { id, type: 'heading'|'paragraph'|'list'|'quote'|'image'|'button'|'divider',
 *     text: { es: '…', en: '…' },   // bloques con texto (list: innerHTML de <li>s)
 *     url: '…' }                    // image (src) y button (href)
 *
 * Se persiste como body[lang] = HTML email-safe (estilos inline) con `data-bid`
 * para poder re-parsear y mantener la correspondencia entre idiomas.
 */

import { LANGS } from "../shared";

let counter = 0;
export const newBlockId = () => `b${Date.now().toString(36)}${(counter++).toString(36)}`;

export const newBlock = (type, extra = {}) => ({
  id: newBlockId(),
  type,
  text: {},
  url: "",
  ...extra,
});

const esc = (s = "") =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// ── Serialización bloque → HTML (estilos inline: compatibilidad email) ───────
const STYLES = {
  heading: "font-size:22px;line-height:1.3;font-weight:700;margin:18px 0 8px;color:#171725;",
  paragraph: "margin:0 0 12px;line-height:1.6;",
  list: "margin:0 0 12px;padding-left:24px;line-height:1.6;",
  quote:
    "margin:12px 0;padding:10px 16px;border-left:3px solid #2934d0;color:#555;font-style:italic;",
  imageWrapBase: "margin:18px 0;",
  imageBase: "border-radius:8px;",
  buttonWrap: "text-align:center;margin:22px 0;",
  button:
    "display:inline-block;background:#2934d0;color:#ffffff;padding:12px 28px;border-radius:6px;font-weight:700;text-decoration:none;",
  divider: "border:none;border-top:1px solid #e4e4e4;margin:22px 0;",
};

const blockToHtml = (block, lang) => {
  const text = block.text?.[lang] || "";
  switch (block.type) {
    case "heading":
      return text ? `<h2 data-bid="${block.id}" style="${STYLES.heading}">${text}</h2>` : "";
    case "paragraph":
      return text ? `<p data-bid="${block.id}" style="${STYLES.paragraph}">${text}</p>` : "";
    case "list":
      return text ? `<ul data-bid="${block.id}" style="${STYLES.list}">${text}</ul>` : "";
    case "quote":
      return text
        ? `<blockquote data-bid="${block.id}" style="${STYLES.quote}">${text}</blockquote>`
        : "";
    case "image": {
      if (!block.url) return "";
      const align = block.align || "center";
      const width = block.width || 100;
      const wrapStyle = `${STYLES.imageWrapBase}text-align:${align};`;
      const imgStyle = `${STYLES.imageBase}max-width:${width}%;`;
      return `<div data-bid="${block.id}" data-btype="image" data-align="${align}" data-width="${width}" style="${wrapStyle}"><img src="${esc(
        block.url
      )}" alt="" style="${imgStyle}"/></div>`;
    }
    case "button":
      return block.url && text
        ? `<div data-bid="${block.id}" data-btype="button" style="${STYLES.buttonWrap}"><a href="${esc(
            block.url
          )}" style="${STYLES.button}">${text}</a></div>`
        : "";
    case "divider":
      return `<hr data-bid="${block.id}" style="${STYLES.divider}"/>`;
    default:
      return "";
  }
};

export const blocksToHtml = (blocks, lang) =>
  (blocks || []).map((b) => blockToHtml(b, lang)).filter(Boolean).join("\n");

const TEXT_TYPES = ["heading", "paragraph", "list", "quote", "button"];

/**
 * blocks → {es: html, …}. Un idioma se incluye si tiene texto en algún bloque, o
 * si el documento no tiene bloques de texto (solo imágenes/separadores) pero ese
 * idioma tiene título (para que el fallback no deje el mail vacío).
 */
export const blocksToBodies = (blocks, titles = {}) => {
  const hasTextBlocks = (blocks || []).some((b) => TEXT_TYPES.includes(b.type));
  const bodies = {};
  LANGS.forEach((lang) => {
    const hasText = (blocks || []).some(
      (b) => TEXT_TYPES.includes(b.type) && (b.text?.[lang] || "").trim() !== ""
    );
    const include = hasText || (!hasTextBlocks && (titles[lang] || "").trim() !== "");
    bodies[lang] = include ? blocksToHtml(blocks, lang) : "";
  });
  return bodies;
};

// ── Parseo HTML → bloques ────────────────────────────────────────────────────
const elementToBlock = (el) => {
  const tag = el.tagName?.toLowerCase();
  const btype = el.getAttribute?.("data-btype");
  if (btype === "image" || tag === "img" || (tag === "div" && el.querySelector?.("img"))) {
    const img = tag === "img" ? el : el.querySelector("img");
    if (!img?.getAttribute("src")) return null;
    const align = el.getAttribute?.("data-align") || undefined;
    const widthAttr = el.getAttribute?.("data-width");
    const width = widthAttr ? Number(widthAttr) : undefined;
    const out = { type: "image", url: img.getAttribute("src") };
    if (align) out.align = align;
    if (width) out.width = width;
    return out;
  }
  if (btype === "button") {
    const a = el.querySelector("a");
    return { type: "button", url: a?.getAttribute("href") || "", text: a?.innerHTML || "" };
  }
  switch (tag) {
    case "h1":
    case "h2":
    case "h3":
      return { type: "heading", text: el.innerHTML };
    case "ul":
    case "ol":
      return { type: "list", text: el.innerHTML };
    case "blockquote":
      return { type: "quote", text: el.innerHTML };
    case "hr":
      return { type: "divider" };
    default:
      return el.textContent?.trim() || el.querySelector("img")
        ? { type: "paragraph", text: el.innerHTML }
        : null;
  }
};

const parseLang = (html) => {
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, "text/html");
  const root = doc.getElementById("root");
  const out = [];
  [...root.children].forEach((el, i) => {
    const parsed = elementToBlock(el);
    if (!parsed) return;
    out.push({ bid: el.getAttribute("data-bid") || `idx-${i}`, ...parsed });
  });
  // HTML sin estructura (texto plano legacy): un único párrafo.
  if (!out.length && html.trim()) {
    out.push({ bid: "legacy", type: "paragraph", text: html });
  }
  return out;
};

/**
 * {es: html, …} → bloques. La estructura sale del idioma con más bloques; el resto
 * de idiomas aporta su texto emparejando por data-bid (o por índice como fallback).
 */
export const bodiesToBlocks = (bodies) => {
  const perLang = {};
  LANGS.forEach((lang) => {
    if (bodies[lang] && bodies[lang].trim()) perLang[lang] = parseLang(bodies[lang]);
  });
  const langsWithContent = Object.keys(perLang);
  if (!langsWithContent.length) return [];

  const refLang = langsWithContent.reduce((a, b) =>
    perLang[b].length > perLang[a].length ? b : a
  );
  const blocks = perLang[refLang].map((p) => ({
    id: newBlockId(),
    bid: p.bid,
    type: p.type,
    url: p.url || "",
    text: {},
    ...(p.align ? { align: p.align } : {}),
    ...(p.width ? { width: p.width } : {}),
  }));

  langsWithContent.forEach((lang) => {
    perLang[lang].forEach((p, i) => {
      let target = blocks.find((b) => b.bid === p.bid && b.type === p.type);
      if (!target) target = blocks[i] && blocks[i].type === p.type ? blocks[i] : null;
      if (target && p.text !== undefined) target.text[lang] = p.text;
      if (target && p.url && !target.url) target.url = p.url;
    });
  });

  return blocks.map(({ bid, ...b }) => b);
};

/** Convierte un body de texto plano (push/inapp) en bloques al cambiar a mail. */
export const plainBodiesToBlocks = (bodies) => {
  const hasAny = LANGS.some((l) => (bodies[l] || "").trim());
  if (!hasAny) return [];
  const block = newBlock("paragraph");
  LANGS.forEach((l) => {
    if ((bodies[l] || "").trim()) block.text[l] = esc(bodies[l]).replace(/\n/g, "<br/>");
  });
  return [block];
};
